import type React from "react";
import { useEffect, useState } from "react";
import { Button } from "~/components/form/Button";
import { CombinedTextFieldAndUnit } from "~/components/form/CombinedTextFieldAndUnit";
import { Field } from "~/components/form/Field";
import { TextField } from "~/components/form/TextField";
import { RedSearchErrorIcon } from "~/components/icons/RedSearchErrorIcon";
import { Modal } from "~/components/overlays/Modal";
import { Heading } from "~/components/typography/Heading";
import { SupportOptions } from "~/features/company/support/CompanySupport";
import type { MeterPointRead } from "~/gen";

export interface MPANErrorModalData {
	error?: {
		mpxn?: FormFieldError;
		eac?: FormFieldError;
	};
	meterPoints?: MeterPointRead[];
	showSupportOptions?: boolean;
	isTraditionalMeter?: boolean;
}

interface MPANErrorModalProps {
	hidden?: boolean;
	mpanOnly?: boolean;
	data: MPANErrorModalData;
	handleSubmitForm?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	handleDismiss: () => void;
}

export const MPANErrorModal = ({
	hidden = true,
	mpanOnly = false,
	data,
	handleSubmitForm,
	handleDismiss,
}: MPANErrorModalProps) => {
	const errors = data?.error?.mpxn?._errors;
	const mpxnLookupFromAddressFailed = errors?.includes(
		"MPAN/MPRN lookup failed",
	);
	const mpxnLookupFromManualInputFailed = errors?.includes(
		"Could not find meter",
	);
	const noSuitableTariffsFound = errors?.includes("No suitable tariffs found");
	const foundMultipleMpxns = errors?.includes("Multiple MPANs found");
	const mpxnHasNoEAC = errors?.includes(
		"Could not get estimated annual consumption for meter",
	);

	const [showSupportOptions, setShowSupportOptions] = useState(false);

	useEffect(() => {
		if (data?.showSupportOptions || mpxnLookupFromManualInputFailed)
			setShowSupportOptions(true);
	}, [data.showSupportOptions, mpxnLookupFromManualInputFailed]);

	const ManualMPANModalContent = () => (
		<div className="flex flex-col items-start gap-3 p-3">
			<div className="flex flex-col gap-1">
				<Heading tagName="h5" styleType="bold">
					<span className="text-secondary block">
						We weren't able to identify your MPAN{!mpanOnly && " or MPRN"}
					</span>
				</Heading>
				<p className="text-secondary-500">
					Please input your MPAN{!mpanOnly && " or MPRN"} to continue
				</p>
			</div>

			<Field errors={mpxnLookupFromAddressFailed ? [] : errors}>
				<TextField
					name="mpxn"
					label={`Enter MPAN${!mpanOnly ? " or MPRN" : ""}`}
					type="number"
					step="1"
					required={true}
					invalid={!mpxnLookupFromAddressFailed && !!data?.error?.mpxn}
				/>
			</Field>

			<div className="flex flex-col gap-3 p-6 bg-secondary-100 rounded-lg w-full">
				<Heading tagName="h5" styleType="semibold">
					How to find your MPAN:
				</Heading>
				<p className="text-secondary-500 text-sm">
					A MPAN is an identifier for your electric meter.
				</p>

				<p className="text-secondary-500 text-sm">
					You can find it on your energy bill, typically in the MPAN box as a
					13-digit number.
				</p>
				<div className="flex border rounded w-full items-stretch border-secondary-200 bg-white select-none">
					<div className="p-4 text-4xl font-medium border-r border-secondary-200 flex items-center">
						S
					</div>
					<div className="flex flex-col grow text-center">
						<div className="grid grid-cols-3 border-b border-secondary-200">
							<div className="p-3 border-r border-secondary-200">04</div>
							<div className="p-3 border-r border-secondary-200">293</div>
							<div className="p-3">999</div>
						</div>
						<div>
							<div className="grid grid-cols-4 border-2 border-warning-300 rounded-br">
								<div className="p-3 border-r border-secondary-200">12</div>
								<div className="p-3 border-r border-secondary-200">3456</div>
								<div className="p-3 border-r border-secondary-200">7890</div>
								<div className="p-3">000</div>
							</div>
						</div>
					</div>
				</div>

				{!mpanOnly && (
					<>
						<Heading tagName="h5" styleType="semibold">
							How to find your MPRN:
						</Heading>
						<p className="text-secondary-500">
							A MPRN is an identifier for your gas meter. <br />
							You MPRN should be located on your energy bill.
						</p>
					</>
				)}
			</div>

			<div className="flex gap-3 w-full flex-col-reverse sm:flex-row">
				<Button
					name="cancel"
					label="Cancel"
					styleType="secondary"
					type="button"
					onClick={handleDismiss}
				/>
				<Button
					name="confirm-mpan"
					label="Confirm"
					styleType="primary-alt"
					type={handleSubmitForm ? "button" : "submit"}
					onClick={handleSubmitForm}
				/>
			</div>
		</div>
	);

	const MultipleMPANModalContent = () => {
		const [selectedMpan, setSelectedMpan] = useState<string | null>(null);
		return (
			<div className="flex flex-col items-start gap-3 p-3">
				<div className="flex flex-col gap-1">
					<Heading tagName="h5" styleType="bold">
						<span className="text-secondary block">
							Please select your MPAN
						</span>
					</Heading>
					<p className="text-secondary-500">
						A MPAN is an identifier for your electric meter. You can find it on
						your energy bill, typically in the MPAN box as a 13-digit number. We
						need it to get your Estimated Annual Consumption.
					</p>
				</div>

				{data.meterPoints?.map((mp, i) => (
					<label
						className="relative overflow-hidden p-4 border border-secondary-200 flex flex-col gap-4 items-start w-[calc(50%-0.25rem)] w-full cursor-pointer rounded-lg has-[:checked]:border-4 has-[:checked]:border-primary hover:bg-secondary-200"
						key={mp.encmpan}
						htmlFor={`mpan-${i}`}
					>
						<div className="flex gap-2 items-center">
							<input
								className="accent-primary h-5 w-5"
								type="radio"
								aria-labelledby={mp.encmpan}
								id={`mpan-${i}`}
								name="encmpxn"
								value={mp.encmpan}
								onChange={() => setSelectedMpan(mp.encmpan)}
							/>
							<span className="font-semibold">MPAN {i + 1}:</span>
						</div>
						<div className="flex border rounded w-full items-stretch border-secondary-200 bg-white select-none">
							<div className="p-4 text-4xl font-medium border-r border-secondary-200 flex items-center">
								S
							</div>
							<div className="flex flex-col grow text-center">
								<div className="grid grid-cols-3 border-b border-secondary-200">
									<div className="p-3 border-r border-secondary-200">xx</div>
									<div className="p-3 border-r border-secondary-200">xxx</div>
									<div className="p-3">xxx</div>
								</div>
								<div>
									<div className="grid grid-cols-4 border-2 border-warning-300 rounded-br">
										<div className="p-3 border-r border-secondary-200">
											{mp.mpan.slice(0, 2)}
										</div>
										<div className="p-3 border-r border-secondary-200">
											{mp.mpan.slice(2, 6)}
										</div>
										<div className="p-3 border-r border-secondary-200">
											{mp.mpan.slice(6, 10)}
										</div>
										<div className="p-3">{mp.mpan.slice(10)}</div>
									</div>
								</div>
							</div>
						</div>
					</label>
				))}

				<div className="flex gap-3 w-full flex-col-reverse sm:flex-row">
					<Button
						name="cancel"
						label="Cancel"
						styleType="secondary"
						type="button"
						onClick={handleDismiss}
					/>
					<Button
						name="confirm-mpan"
						label="Confirm"
						styleType="primary-alt"
						type={handleSubmitForm ? "button" : "submit"}
						disabled={!selectedMpan}
						onClick={handleSubmitForm}
					/>
				</div>
			</div>
		);
	};

	const ManualEACInputModalContent = () => {
		const DEFAULT_EAC = 13_000;
		const [eac, setEac] = useState<number>();
		const [useDefaultEac, setUseDefaultEac] = useState(false);
		const toggleUseDefaultEac = () => {
			if (!useDefaultEac) setEac(DEFAULT_EAC);
			setUseDefaultEac(!useDefaultEac);
		};
		const handleEacInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
			const value = e.target.value.replace(/[\D]/g, "");
			if (value === "") setEac(undefined);
			const numValue = Number(value);
			if (numValue !== DEFAULT_EAC) setUseDefaultEac(false);
			setEac(numValue === 0 || Number.isNaN(numValue) ? undefined : numValue);
		};
		return (
			<div className="flex flex-col items-start gap-3 p-3">
				<div className="flex flex-col gap-1">
					<Heading tagName="h5" styleType="bold">
						<span className="text-secondary block">
							We could not retrieve your estimated annual consumption
						</span>
					</Heading>
					<p className="text-secondary-500">
						Please enter your estimated annual consumption in kWh or use a
						default value
					</p>
				</div>

				<div>
					<Field>
						<CombinedTextFieldAndUnit
							name="eac"
							label="Your estimated annual consumption"
							step="1"
							unit="kWh"
							value={eac}
							onChange={handleEacInputChange}
							required={true}
						/>
					</Field>

					<div className="flex gap-2 items-start mb-4">
						<div
							className={`flex rounded-full translate-y-[0.2rem] p-px w-8 cursor-pointer ${useDefaultEac ? "bg-primary-500 justify-end" : "bg-gray-500 justify-start"}`}
							onClick={toggleUseDefaultEac}
							onKeyUp={toggleUseDefaultEac}
						>
							<div className="rounded-full w-4 h-4 bg-white" />
						</div>{" "}
						Use default value
					</div>

					{data.meterPoints?.[0].encmpan && (
						<input
							type="hidden"
							name="encmpxn"
							value={data.meterPoints?.[0].encmpan}
						/>
					)}
					{!data.meterPoints?.[0].encmpan && data.meterPoints?.[0].mpan && (
						<input
							type="hidden"
							name="mpxn"
							value={data.meterPoints?.[0].mpan}
						/>
					)}
				</div>
				<div className="flex gap-3 w-full flex-col-reverse sm:flex-row">
					<Button
						name="cancel"
						label="Cancel"
						styleType="secondary"
						type="button"
						onClick={handleDismiss}
					/>
					<Button
						name="confirm-mpan"
						label="Confirm"
						styleType="primary-alt"
						disabled={eac === undefined}
						type={handleSubmitForm ? "button" : "submit"}
						onClick={handleSubmitForm}
					/>
				</div>
			</div>
		);
	};

	const SupportOptionsModalContent = () => (
		<div className="flex flex-col items-center gap-4 p-3">
			<div className="bg-primary-100 ring-[6px] ring-primary-50 rounded-full p-1.5">
				<RedSearchErrorIcon />
			</div>
			<div className="flex flex-col gap-1 items-center">
				{data.isTraditionalMeter ? (
					<>
						<Heading tagName="h5" styleType="semibold">
							You seem to have a traditional meter
						</Heading>
						<p className="text-secondary-500 text-balance text-center">
							It appears that you don’t have a smart meter. To make the best use
							of Tariff Tribe we recommend getting one (please contact your
							current energy supplier about how to get one installed). In the
							meantime a member of the team will be in touch about next steps.
							<br />
							<br />
							You can also reach out to us directly through these options:
						</p>
					</>
				) : (
					<>
						<Heading tagName="h5" styleType="semibold">
							We couldn't find your meter
						</Heading>
						<p className="text-secondary-500 text-balance text-center">
							Please try a different MPAN/MPRN or contact our support team
						</p>
					</>
				)}
			</div>

			<div className="w-full py-2">
				<SupportOptions showReportProblemLink={false} />
			</div>

			<div className="flex gap-3 w-full flex-col-reverse sm:flex-row">
				<Button
					name="cancel"
					label="Cancel"
					styleType="secondary"
					type="button"
					onClick={handleDismiss}
				/>
				<Button
					name="confirm-mpan"
					label="Retry"
					styleType="primary-alt"
					type="button"
					onClick={() => setShowSupportOptions(false)}
				/>
			</div>
		</div>
	);

	return (
		<Modal hidden={hidden} handleDismiss={handleDismiss}>
			{showSupportOptions ? (
				<SupportOptionsModalContent />
			) : foundMultipleMpxns ? (
				<MultipleMPANModalContent />
			) : mpxnHasNoEAC ? (
				<ManualEACInputModalContent />
			) : noSuitableTariffsFound ? (
				<h3>No suitable tariffs found</h3>
			) : (
				<ManualMPANModalContent />
			)}
		</Modal>
	);
};
